import { Colaborador } from "../shared/model/vacaciones/vacacion.model";

/**
 * El buscador de los selectores de persona del calendario de vacaciones
 * (15/10/2026). Irving: el campo Colaborador de «Agregar/Editar vacaciones»
 * era un <select> nativo con una lista inmensa y sin búsqueda. Ahora es un
 * mat-select con ngx-mat-select-search, como el de Parámetros del BSC, y lo
 * que decide qué opciones salen vive aquí, sin Angular, para probarlo solo.
 */

/** Una persona del selector: su id, el nombre que se ve y ese nombre listo para buscar. */
export interface OpcionPersona {
  idUsuario: number;
  nombre: string;
  /** El nombre sin acentos ni mayúsculas (normalizaBusqueda). */
  clave: string;
}

/** Sin acentos ni mayúsculas: «López» y «lopez» son lo mismo para el buscador. */
export function normalizaBusqueda(texto: string | null | undefined): string {
  return (texto ?? "").normalize("NFD").replace(/\p{M}/gu, "").toLowerCase().trim();
}

/**
 * Los colaboradores como opciones, en orden alfabético por el nombre que se
 * ve (con las reglas del español: la «Á» va con la «A»). Quien llega sin id
 * no se puede elegir y se queda fuera; sin nombre, se le pone uno para que
 * la opción no salga en blanco.
 */
export function opcionesDePersonas(colaboradores: readonly Colaborador[]): OpcionPersona[] {
  const opciones: OpcionPersona[] = [];
  for (const c of colaboradores) {
    if (c.idUsuario != null) {
      const nombre = (c.nombre ?? "").trim() || `Usuario ${c.idUsuario}`;
      opciones.push({ idUsuario: c.idUsuario, nombre, clave: normalizaBusqueda(nombre) });
    }
  }
  return opciones.sort((a, b) => a.nombre.localeCompare(b.nombre, "es"));
}

/**
 * Las opciones que tienen CADA palabra escrita en cualquier parte del
 * nombre, sin acentos ni mayúsculas («per lop» encuentra a «Pérez López»).
 * Sin nada escrito salen todas (el mismo arreglo, para no repintar la
 * lista). Quien ya está elegido se queda aunque no coincida, para que el
 * select no pierda lo que muestra, pero al final: la primera opción es la
 * primera coincidencia y Enter elige a quien se buscó.
 */
export function filtraPersonas(
  opciones: OpcionPersona[],
  texto: string | null | undefined,
  elegido?: number | null,
): OpcionPersona[] {
  const palabras = normalizaBusqueda(texto).split(/\s+/).filter((p) => p.length > 0);
  if (!palabras.length) {
    return opciones;
  }
  const salen: OpcionPersona[] = [];
  let laElegida: OpcionPersona | undefined;
  for (const op of opciones) {
    if (palabras.every((p) => op.clave.includes(p))) {
      salen.push(op);
    } else if (op.idUsuario === elegido) {
      laElegida = op;
    }
  }
  if (laElegida) {
    salen.push(laElegida);
  }
  return salen;
}
